/**
 * Row styles and fold-state storage the sidebar modules share. Lived inside
 * Sidebar.tsx until #2089 added a second file (MyLinks.tsx) that renders rows on
 * the same rail; one copy of each keeps the two visually identical.
 */
import { patchUiPrefs } from "@/hooks/use-settings";

/** Shared row style for module headers + single-page module links. Cloudflare keeps
 * module labels in the DEFAULT foreground (not muted) — muted is reserved for the
 * group labels — with a neutral grey pill on hover/active. #4334: 44 px tall in the
 * phone drawer (touch minimum); the lg rail keeps its density. */
export function moduleRowClass(active: boolean) {
  return `flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors lg:min-h-0 ${
    active
      ? "bg-sidebar-accent text-sidebar-accent-foreground"
      : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
  }`;
}

/** Shared icon-button style for the collapsed rail. */
export function railIconClass(active: boolean) {
  return `mx-auto flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
    active
      ? "bg-sidebar-accent text-sidebar-accent-foreground"
      : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
  }`;
}

/** A rail sub-item's classes (the kumo menu-sub-button shape, see Sidebar.tsx SubItem).
 * #4334: 44 px below lg (phone drawer tap target), 32 px on the lg rail. */
export function subItemClass(active: boolean, nested = false) {
  return `${nested ? "ml-5 leading-tight" : "ml-2"} flex min-h-11 items-center gap-2 rounded-lg px-3 py-1 text-sm font-medium transition-colors lg:min-h-8 ${
    active
      ? "bg-sidebar-accent text-sidebar-accent-foreground"
      : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
  }`;
}

export const SECTION_COLLAPSED_KEY = "crosbe-nav-sections-collapsed";

// #373: localStorage is the fast local cache; the user's `ui_prefs` blob on the server
// is the cross-device source of truth. #3072: the write sends the `collapsed` slice
// ALONE and the server merges it, so no last-known-blob base is held here at all and
// another tab's table or ICC prefs cannot be clobbered by a stale one.
let savePrefsTimer: ReturnType<typeof setTimeout> | undefined;

export function readCollapsedSections(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(SECTION_COLLAPSED_KEY) || "{}");
  } catch {
    return {};
  }
}

// Debounced best-effort server sync. localStorage already holds the live state, so a
// failed/slow write never blocks the UI: the next load just falls back to the local cache.
function queueServerSync(collapsed: Record<string, boolean>) {
  if (savePrefsTimer) clearTimeout(savePrefsTimer);
  savePrefsTimer = setTimeout(() => {
    patchUiPrefs({ collapsed }).catch(() => {
      // best-effort; localStorage remains the source for this device
    });
  }, 600);
}

export function writeCollapsedSection(label: string, value: boolean) {
  try {
    const map = readCollapsedSections();
    map[label] = value;
    localStorage.setItem(SECTION_COLLAPSED_KEY, JSON.stringify(map));
    queueServerSync(map);
  } catch {
    // ignore
  }
}
